import { Injectable, inject } from "@angular/core";
import {
  HttpClient,
  HttpErrorResponse,
  HttpResponse,
} from "@angular/common/http";
import { NativePageTransitions } from "@awesome-cordova-plugins/native-page-transitions/ngx";
import { ModalController, NavController, Platform } from "@ionic/angular";
import {
  catchError,
  from,
  map,
  Observable,
  of,
  ReplaySubject,
  shareReplay,
  switchMap,
  tap,
} from "rxjs";
import {
  applicationClientId,
  clientSecret,
  endpointAuth,
  endpointBase,
} from "src/app/core/api-config";
import { AuthResponse, Response } from "../models/response";
import { User } from "../models/user";
import { LinkService } from "./link.service";
import { StorageService } from "./storage.service";

@Injectable({
  providedIn: "root",
})
export class SharedService {
  access_token?: string; //Auth token
  refresh_token?: string; //Auth refreshToken
  /** True enquanto a troca de token esta em voo (ver refresh()). */
  private refreshing = false;
  oneSignalId?: string;
  oneSignalToken?: string;

  user$: ReplaySubject<User | undefined> = new ReplaySubject<User | undefined>(
    1,
  );
  user?: User; //LoggedUser

  web = false;

  /**
   * Refresh em voo. O backend (django-oauth-toolkit) rotaciona o refresh_token
   * a cada troca e revoga o anterior na hora, entao dois 401 simultaneos que
   * disparassem duas trocas queimariam o token da segunda: todos esperam a
   * mesma chamada.
   */
  private refresh$?: Observable<{ status: boolean }>;

  private http = inject(HttpClient);

  constructor(
    public platform: Platform,
    public storage: StorageService,
    public link: LinkService,
    public navCtrl: NavController,
    public nativePageTransitions: NativePageTransitions,
    public modalController: ModalController,
  ) {
    this.user$.next(undefined);
    this.listenUser();
  }

  get<T>(endpoint: string): Observable<Response<T>> {
    return this.makeRequest<T>("GET", endpoint);
  }

  post<T>(
    endpoint: string,
    data: any,
    isAuth = false,
    headers: {
      [key: string]: string;
    } = null,
  ): Observable<Response<T>> {
    return from(this.getUpdatedData(data)).pipe(
      switchMap((updatedData) => {
        return this.makeRequest<T>(
          "POST",
          endpoint,
          updatedData,
          isAuth,
          headers,
        );
      }),
    );
  }

  put<T>(
    endpoint: string,
    data: any,
    headers: {
      [key: string]: string;
    } = null,
  ): Observable<Response<T>> {
    return from(this.getUpdatedData(data)).pipe(
      switchMap((updatedData) => {
        return this.makeRequest<T>(
          "PUT",
          endpoint,
          updatedData,
          false,
          headers,
        );
      }),
    );
  }

  patch<T>(
    endpoint: string,
    data: any,
    headers: {
      [key: string]: string;
    } = null,
  ): Observable<Response<T>> {
    return from(this.getUpdatedData(data)).pipe(
      switchMap((updatedData) => {
        return this.makeRequest<T>(
          "PATCH",
          endpoint,
          updatedData,
          false,
          headers,
        );
      }),
    );
  }

  delete<T>(endpoint: string, data?: any): Observable<Response<T>> {
    return from(this.getUpdatedData(data)).pipe(
      switchMap((updatedData) => {
        return this.makeRequest<T>("DELETE", endpoint, updatedData);
      }),
    );
  }

  saveToken(access_token: string, refresh_token: string) {
    this.access_token = access_token;
    this.refresh_token = refresh_token;
    this.storage.set("auth", {
      access_token: this.access_token,
      refresh_token: this.refresh_token,
    });
  }

  signin(
    username: string,
    password: string,
  ): Observable<Response<AuthResponse>> {
    const data = {
      username: username,
      password: password,
      grant_type: "password",
      client_id: applicationClientId,
      client_secret: clientSecret,
    };
    return this.post<AuthResponse>("token/", data, true);
  }

  resetUser() {
    this.access_token = "";
    this.refresh_token = "";
    this.user = undefined;
    this.storage.set("auth", null);
    this.user$.next(undefined);
  }

  logout(): Observable<Response<AuthResponse>> {
    // RFC 7009: revogar o refresh derruba junto o access_token emitido com ele.
    // O "auth/logout" antigo montava .../auth/auth/logout (404), entao o token
    // continuava valido no servidor ate expirar mesmo depois do logout.
    const data = {
      client_id: applicationClientId,
      client_secret: clientSecret,
      token: this.refresh_token || this.access_token || "",
      token_type_hint: this.refresh_token ? "refresh_token" : "access_token",
    };
    return this.post<any>("revoke_token/", data, true).pipe(
      tap(() => {
        this.resetUser();
        this.navCtrl.navigateRoot("/tabs/home");
      }),
      catchError((error) =>
        this.handleErrorRequest(error, () => this.logout()),
      ),
    );
  }

  private refresh(): Observable<{ status: boolean }> {
    if (this.refresh$) {
      return this.refresh$;
    }

    const data = {
      refresh_token: this.refresh_token,
      client_id: applicationClientId,
      client_secret: clientSecret,
      grant_type: "refresh_token",
    };

    // Mesmo endpoint OAuth2 do login (authBase + token/). O antigo "auth"
    // montava .../auth/auth, que nao existe no backend.
    this.refreshing = true;
    this.refresh$ = this.post<AuthResponse>("token/", data, true).pipe(
      map((response) => {
        const access_token = response.data?.access_token;
        if (!access_token) {
          return { status: false };
        }
        // Resposta do OAuth2 vem em snake_case e ja traz o refresh rotacionado;
        // saveToken e o unico ponto que grava, para o auth.guard reler as
        // mesmas chaves no proximo boot.
        this.saveToken(
          access_token,
          response.data?.refresh_token ?? this.refresh_token,
        );
        return { status: true };
      }),
      catchError((error) => {
        console.error("Error refresh: ", error);
        return of({ status: false });
      }),
      tap(() => {
        this.refreshing = false;
        this.refresh$ = undefined;
      }),
      shareReplay(1),
    );

    return this.refresh$;
  }

  private makeRequest<T>(
    method: string,
    endpoint: string,
    data?: any,
    isAuth?: boolean,
    headers: any = null,
  ): Observable<Response<T>> {
    const sent_token = this.access_token;
    const authorizationHeader: { [key: string]: string } = {
      Authorization: `Bearer ${sent_token ? sent_token : ""}`,
      ...headers,
    };
    // Multipart: aceita objeto plano ou FormData. Converte para FormData e
    // remove o Content-Type manual para o browser definir o boundary correto
    // (sem isso o servidor responde "Invalid boundary in multipart: None").
    let body = data;
    if (authorizationHeader["Content-Type"]?.startsWith("multipart/form-data")) {
      body = data instanceof FormData ? data : this.objectToFormData(data);
      delete authorizationHeader["Content-Type"];
    }
    const url = `${!isAuth ? endpointBase : endpointAuth}/${endpoint}`;

    return this.http
      .request<Response<T>>(method, url, {
        body,
        headers: authorizationHeader,
        observe: "response",
      })
      .pipe(
        map((response: HttpResponse<Response<T>>) => {
          const body = response.body;
          return {
            httpStatus: body?.message_code ? body.message_code : response.status,
            status: body?.message_code ? false : true,
            message: body?.message || "",
            data: body?.data
              ? ({ ...body, ...body.data } as any)
              : body?.message_code
                ? null
                : (body as T),
          } as Response<T>;
        }),
        catchError((error: HttpErrorResponse) => {
          return this.handleErrorRequest<T>(
            error,
            () => this.makeRequest(method, endpoint, data, isAuth, headers),
            sent_token,
          );
        }),
      );
  }

  private handleErrorRequest<T>(
    error: HttpErrorResponse,
    originalRequest: () => Observable<Response<T>>,
    sent_token?: string,
  ): Observable<Response<T>> {
    // O backend mata o access anterior assim que o refresh roda, entao
    // requisicoes que ja estavam no ar voltam 401 com token velho. Se o atual
    // ja e outro, alguem renovou: basta repetir. Renovar de novo rotacionaria o
    // refresh a toa e mataria o access recem-nascido, em cascata.
    if (error.status === 401 && sent_token && this.access_token !== sent_token) {
      return originalRequest();
    }

    // !refreshing: o 401 da propria troca de token nao pode disparar outra.
    if (error.status === 401 && this.refresh_token && !this.refreshing) {
      return this.refresh().pipe(
        switchMap((refreshResponse) => {
          if (refreshResponse.status) {
            return originalRequest();
          } else {
            this.logout().subscribe();
            return of({
              httpStatus: error.status,
              status: false,
              message: "Token refresh failed",
              data: error.error,
            } as Response<T>);
          }
        }),
      );
    } else {
      const errorBody = error.error as Response<T> | undefined;
      return of({
        httpStatus: error.status,
        status: false,
        message: errorBody && errorBody.message ? errorBody.message : "",
        data: errorBody,
      } as Response<T>);
    }
  }

  private listenUser() {
    this.user$.subscribe((u) => {
      this.user = u;
    });
  }

  // Serializa um objeto plano em FormData para requisições multipart.
  // Files/Blobs vão como anexo (preservando o filename); demais valores como
  // string. null/undefined são ignorados.
  private objectToFormData(data: any): FormData {
    const formData = new FormData();
    if (!data || typeof data !== "object") {
      return formData;
    }
    Object.keys(data).forEach((key) => {
      const value = data[key];
      if (value === null || value === undefined) {
        return;
      }
      formData.append(key, value instanceof Blob ? value : String(value));
    });
    return formData;
  }

  private async getUpdatedData(data: any) {
    if (!data) {
      data = {};
    }
    if (this.oneSignalId) {
      data.oneSignalId = this.oneSignalId;
    }
    if (this.oneSignalToken) {
      data.oneSignalToken = this.oneSignalToken;
    }
    return data;
  }
}
